import React, { useEffect } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import MainHeader from './components/ui/MainHeader/MainHeader';
import Loading from './components/ui/Loading/Loading';
import HousePage from './pages/HousePage/HousePage';
import HousesPage from './pages/HousesPage/HousesPage';
import TenantsPage from './pages/TenantsPage/TenantsPage';
import { ExportsPage } from './pages/ExportsPage/ExportsPage';
import ImportsPage from './pages/ImportsPage';
import PageNotFound from '../PageNotFound/container';
import PersonalAccountsPage from './pages/PersonalAccountsPage/PersonalAccountsPage';
import RequestCardDrawer from '../../components/RequestCardDrawer/RequestCardDrawer';
import NewDocumentContainer from '../new-wdocument.container';
import WarrantyInfoContainer from '../warranty-info-form.container';
import WarrantyContainer from '../warranty-form.container';
import ExecuteTimeContainer from '../set-execute-time-panel.container';
import ExecuterContainer from '../set-executer-panel.container';
import MasterContainer from '../set-master-panel.container';
import ServiceContainer from '../set-service-panel.container';
import AddressContainer from '../set-address-panel.container';
import useCompanies from '../../queries/useCompanies';
import useHousingStock from './queries/useHousingStock';
import useHousingStockHouses from './queries/useHousingStockHouses';
import useHousingStockMatch from './hooks/useHousingStockMatch';
import {
  resetSearchFieldValue,
  setCurrentCompany,
  setHousesTablePage,
  setHousesView,
  setVisibleTransportForm,
} from './store/actions/housingStockActions';
import { links } from './constants/links';
import { texts } from './constants/texts';
import { housesViews } from './constants/housesViews';
import useHouseStages from './queries/useHouseStages';
import useRequestCardMutationFns from '../../hooks/useRequestCardMutationFns';
import useResetPageState from '../../crossPageInteractions/hooks/useResetPageState';
import useSetRefreshFn from '../../hooks/useSetRefreshFn';
import { getQueryKeys } from './queries/queryKeys';
import DocFormModal from './components/ui/modals/DocFormModal';
import MeterModal from './components/ui/modals/MeterModal/MeterModal';
import styles from './HousingStockContainer.module.scss';
import ChangingPage from './pages/ChangingPage/ChangingPage';
import ChangingAllPage from './pages/ChangingAllPage/ChangingAllPage';
import ChangingAllPageTwo from './pages/ChangingAllPageTwo/ChangingAllPageTwo';
import TemplateModal from './components/ui/TemplateModal/TemplateModal';
import ReportRSOPage from './pages/ReportRSOPage';
import {
  getCompanyFromUrlOrSavedOrFirst,
  saveSelectedCompanyId,
} from '../../helpers/companyStorage';
import { ModalTransportTenant } from './components/ui/TransportTenant';
import TransportModal from '../../components/TransportModal/TransportModal';

const getRoutePath = (pathname) =>
  `${links.rootWithCompany}/:companyId/${pathname}`;

const HousingStockContainer = () => {
  const match = useHousingStockMatch();

  const queryClient = useQueryClient();

  const dispatch = useDispatch();

  const currentCompany = useSelector(
    (state) => state.housingStockStore.currentCompany
  );
  const currentHouse = useSelector(
    (state) => state.housingStockStore.currentHouse
  );
  const currentFlat = useSelector(
    (state) => state.housingStockStore.currentFlat
  );

  const visibleTransportForm = useSelector(
    (state) => state.housingStockStore.visibleTransportForm
  );

  const handleCloseTransportForm = () => {
    dispatch(setVisibleTransportForm(null));
  };

  const { data: companies } = useCompanies({
    placeholderData: [],
  });

  useHousingStock(
    { companyId: currentCompany?.id },
    { enabled: Boolean(currentCompany?.id) }
  );

  useHousingStockHouses(
    { companyId: currentCompany?.id },
    { enabled: Boolean(currentCompany?.id) }
  );

  useSetRefreshFn(() => {
    const keys = [
      // houses page
      getQueryKeys.housingStock(currentCompany?.id),
      getQueryKeys.housingStockHouses({
        companyId: currentCompany?.id,
      }),

      // house page
      getQueryKeys.housingStockHouses({
        houseId: currentHouse?.houseId,
      }),
      getQueryKeys.flatsGrid(currentHouse?.houseId),
      getQueryKeys.houseInfo(currentHouse?.houseId),
      getQueryKeys.houseHead(currentHouse?.houseId),
      getQueryKeys.houseComment(currentHouse?.houseId),
      getQueryKeys.houseBalance(currentHouse?.houseId),
      getQueryKeys.houseAttachments(currentHouse?.houseId),
      getQueryKeys.housingStockRequests(currentHouse?.houseId),
      getQueryKeys.houseCameras(currentHouse?.houseId),

      //flat page
      getQueryKeys.flatAccounts(currentFlat?.addressId),
      getQueryKeys.flatAttachments(currentFlat?.addressId),
      getQueryKeys.flatComment(currentFlat?.addressId),
      getQueryKeys.flatMeters(currentFlat?.addressId),
      getQueryKeys.housingStockRequests(undefined, currentFlat?.addressId),
    ];

    keys.forEach((key) => queryClient.invalidateQueries(key));
  }, [currentCompany, currentHouse, currentFlat]);

  useHouseStages({ staleTime: Infinity });

  useResetPageState({ page: 'Requests' });
  useResetPageState({ page: 'Questions' });

  const {
    handleMasterChange,
    handleExecuterChange,
    handleAddressChange,
    handleServiceChange,
    handleExecutionTimeChange,
    handleWarrantyInfoChange,
    handleWarrantyDocsAdd,
  } = useRequestCardMutationFns();

  // set current company
  useEffect(() => {
    if (companies?.length) {
      const urlCompanyId = match?.params.companyId
        ? Number(match.params.companyId)
        : null;

      const company = getCompanyFromUrlOrSavedOrFirst(companies, urlCompanyId);

      if (company) {
        dispatch(setCurrentCompany(company));
        saveSelectedCompanyId(company.id);
      }
    }
  }, [match, companies, dispatch]);

  // reset current company
  useEffect(() => {
    return () => {
      dispatch(setHousesView(housesViews.list));
      dispatch(setHousesTablePage(0));
      dispatch(resetSearchFieldValue());
    };
  }, [dispatch]);

  const houseData = useSelector(
    (state) => state.housingStockStore.templateModalData
  );

  return (
    <section
      className={styles.container}
      id="HousingStockContainer"
    >
      <Switch>
        <Route
          exact
          path={getRoutePath(links.changing)}
        >
          <ChangingPage />
        </Route>
        <Route
          exact
          path={getRoutePath(links.changingAll)}
        >
          <ChangingAllPage />
        </Route>
        <Route
          exact
          path={getRoutePath(links.changingAllTwo)}
        >
          <ChangingAllPageTwo />
        </Route>
        <Route
          exact
          path={getRoutePath(`${links.houses}/${links.house.main}`)}
        >
          <MainHeader className={styles.header} />
          <HousePage />
        </Route>
        <Route
          exact
          path={getRoutePath(links.houses)}
        >
          <MainHeader className={styles.header} />
          <HousesPage />
        </Route>
        <Route
          exact
          path={getRoutePath(links.tenants)}
        >
          <MainHeader className={styles.header} />
          <TenantsPage />
        </Route>
        <Route
          exact
          path={getRoutePath(links.personalAccounts)}
        >
          <MainHeader className={styles.header} />
          <PersonalAccountsPage />
        </Route>
        <Route
          exact
          path={getRoutePath(links.exports)}
        >
          <MainHeader className={styles.header} />
          <ExportsPage />
        </Route>
        <Route
          exact
          path={getRoutePath(links.imports)}
        >
          <MainHeader className={styles.header} />
          {Boolean(currentCompany) && <ImportsPage company={currentCompany} />}
        </Route>
        <Route
          exact
          path={getRoutePath(links.reportRSO)}
        >
          <MainHeader className={styles.header} />
          <ReportRSOPage />
        </Route>
        <Route
          exact
          path={links.root}
        >
          <MainHeader className={styles.header} />
          {!currentCompany?.id ? (
            <Loading
              size="large"
              tip={texts.loading}
            />
          ) : (
            <Redirect
              to={`${links.rootWithCompany}/${currentCompany?.id}/${links.houses}`}
            />
          )}
        </Route>
        <Route path="*">
          <MainHeader className={styles.header} />
          <PageNotFound />
        </Route>
      </Switch>
      <RequestCardDrawer />
      <WarrantyContainer />
      <NewDocumentContainer onAddDocument={handleWarrantyDocsAdd} />
      <WarrantyInfoContainer onWarrantyInfoChange={handleWarrantyInfoChange} />
      <ExecuteTimeContainer updateExecuteTime={handleExecutionTimeChange} />
      <ExecuterContainer updateExecuter={handleExecuterChange} />
      <MasterContainer updateMaster={handleMasterChange} />
      <ServiceContainer updateService={handleServiceChange} />
      <AddressContainer updateAddress={handleAddressChange} />
      <DocFormModal />
      <MeterModal />
      <ModalTransportTenant />
      <TransportModal
        visible={!!visibleTransportForm}
        values={visibleTransportForm}
        onCancel={handleCloseTransportForm}
      />
      {houseData && <TemplateModal houseData={houseData} />}
    </section>
  );
};

export default HousingStockContainer;
